import { inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { catchError, finalize, Observable, of, shareReplay, switchMap, throwError, timer, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

let refreshInFlight$: Observable<unknown> | null = null;

export const authExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  const http = inject(HttpClient);

  return next(req).pipe(
    catchError((error) => {
      if (!shouldHandleAuthExpired(req.url, error)) {
        return throwError(() => error);
      }

      return refreshSession(http).pipe(
        switchMap(() => timer(300)),
        switchMap(() => next(withCredentialedFetch(req))),
        catchError((retryError) => verifyBeforeRedirect(http, retryError)),
      );
    }),
  );
};

function refreshSession(http: HttpClient): Observable<unknown> {
  if (!refreshInFlight$) {
    refreshInFlight$ = http.post(`${environment.apiGatewayUrl}/api/v1/user/refresh`, {}, {
      responseType: 'text',
      credentials: 'include',
    }).pipe(
      timeout(8000),
      finalize(() => {
        refreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  return refreshInFlight$;
}

function verifyBeforeRedirect(http: HttpClient, retryError: unknown) {
  return http.get<{ authenticated?: boolean | null }>(`${environment.apiGatewayUrl}/api/v1/user/auth-verify`, {
    credentials: 'include',
  }).pipe(
    catchError(() => of(null)),
    switchMap((session) => {
      if (!session?.authenticated) {
        redirectToLogin();
      }

      return throwError(() => retryError);
    }),
  );
}

function withCredentialedFetch(req: HttpRequest<unknown>): HttpRequest<unknown> {
  return req.credentials === 'include'
    ? req
    : req.clone({ withCredentials: false, credentials: 'include' });
}

function shouldHandleAuthExpired(url: string, error: unknown): boolean {
  return typeof window !== 'undefined'
    && error instanceof HttpErrorResponse
    && (error.status === 401 || error.status === 419)
    && !isLoginPage()
    && !isBypassedAuthEndpoint(url);
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const loginUrl = new URL('/login', environment.ownerAppUrl);
  loginUrl.searchParams.set('returnUrl', currentReturnPath());
  window.location.href = loginUrl.toString();
}

function isLoginPage(): boolean {
  const path = window.location.pathname;
  return path === '/login' || path.startsWith('/login/');
}

function isBypassedAuthEndpoint(url: string): boolean {
  return (
    url.includes('/api/v1/user/auth-verify') ||
    url.includes('/api/v1/user/login') ||
    url.includes('/api/v1/user/pre-authenticate') ||
    url.includes('/api/v1/user/verify') ||
    url.includes('/api/v1/user/send-token') ||
    url.includes('/api/v1/user/mfa/delivery-capabilities') ||
    url.includes('/api/v1/user/refresh') ||
    url.includes('/api/v1/user/logout')
  );
}

function currentReturnPath(): string {
  if (typeof window === 'undefined') {
    return '/dashboard';
  }

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return safeOwnerReturnPath(path);
}

function safeOwnerReturnPath(value: string | null | undefined): string {
  if (!value || value.startsWith('http://') || value.startsWith('https://') || value.startsWith('//')) {
    return '/dashboard';
  }

  const path = value.startsWith('/') ? value : `/${value}`;
  if (path === '/login' || path.startsWith('/login?') || path.startsWith('/login/')) {
    return '/dashboard';
  }

  return path;
}
